import React, { FC, useEffect, useState, FormEvent, HTMLProps } from "react";
import cn from "classnames";
import stl from "./index.module.scss";
import { isPresent } from "../../utils";

type Value = number;
interface Props extends Omit<HTMLProps<HTMLDivElement>, "value" | "onChange"> {
  value?: number;
  onChange?: (v: number) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  maxDisabled?: boolean;
  minDisabled?: boolean;
}

const minMax = (v: number, min?: number, max?: number): number => {
  if (isPresent(min) && v <= min) return min;
  if (isPresent(max) && v >= max) return max;
  return v;
};

const Quantity: FC<Props> = ({
  min,
  max,
  value,
  onChange,
  disabled,
  maxDisabled,
  minDisabled,
  className,
  ...props
}) => {
  const [localValue, setLocalValue] = useState<Value>(0);

  const displayValue = value ?? localValue;

  const onChanges = (v: Value) => {
    if (maxDisabled && v > displayValue) return;
    if (minDisabled && v < displayValue) return;
    onChange?.(v);
    setLocalValue(v);
  };

  useEffect(() => {
    const adjustedVal = minMax(displayValue, min, max);
    if (adjustedVal !== displayValue) {
      onChanges(minMax(displayValue, min, max));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayValue, min, max]);

  const increase = () => {
    onChanges(displayValue + 1);
  };

  const decrease = () => {
    onChanges(displayValue - 1);
  };

  const onInput = ({
    currentTarget: { value },
  }: FormEvent<HTMLInputElement>) => {
    onChanges(value.length > 0 ? parseInt(value) : 0);
  };

  return (
    <div {...props} className={cn(className, stl.block)}>
      <button
        className={stl.decrease}
        onClick={decrease}
        disabled={
          disabled || minDisabled || (isPresent(min) && displayValue <= min)
        }
      />
      <input
        // readOnly
        disabled={disabled}
        className={stl.input}
        value={displayValue}
        onChange={onInput}
        type="number"
      />
      <button
        className={stl.increase}
        onClick={increase}
        disabled={
          disabled || maxDisabled || (isPresent(max) && displayValue >= max)
        }
      />
    </div>
  );
};

export default Quantity;
